import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG } from '../../config/apiConfig'
import { showToast } from '../../utils/messageUtil';
import { useUser } from '../../contexts/UserContext'
import './index.css'

// 缓存管理函数已从 auth_access_util.js 导入

export default function CarApproval() {

    const navigate = useNavigate()
    const { userInfo, loading: userLoading, refreshUserInfo } = useUser()

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.user_id || userInfo.open_id
        const hasName = userInfo.name && userInfo.name.trim() !== ''
        const hasMobile = userInfo.mobile && userInfo.mobile.trim() !== ''
        const hasDepartment = userInfo.department || userInfo.department_name
        return hasUserId && hasName && hasMobile && hasDepartment
    }

    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('用车审批页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    const [approvals, setApprovals] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    // 筛选条件
    const [filters, setFilters] = useState({
        plateNumber: ''
    })

    // 弹窗状态
    const [approvalModalVisible, setApprovalModalVisible] = useState(false)
    const [selectedApproval, setSelectedApproval] = useState(null)
    const [approvalOpinion, setApprovalOpinion] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [drivers, setDrivers] = useState([])
    const [selectedDriverId, setSelectedDriverId] = useState(null)
    const [loadingDrivers, setLoadingDrivers] = useState(false)
    const [driveType, setDriveType] = useState(1)
    
    // 改派弹窗状态
    const [reassignModalVisible, setReassignModalVisible] = useState(false)
    const [reassignForm, setReassignForm] = useState({
        expectedStartTime: '',
        expectedEndTime: '',
        plateNumber: '',
        vehicleId: ''
    })
    const [availableVehicles, setAvailableVehicles] = useState([])
    const [loadingVehicles, setLoadingVehicles] = useState(false)
    
    const containerRef = useRef(null)

    // 获取审批数据
    const fetchApprovals = async (page = 1) => {
        try {
            if (page === 1) {
                setLoading(true)
            } else {
                setLoadingMore(true)
            }
            setError(null)

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getCarApplicationList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                page: page,
                rows: 10,
                ...filters
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.success) {
                const approvalData = response.data.result.rows || []
                const total = response.data.result.total || 0
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)

                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)

                if (page === 1) {
                    setApprovals(approvalData)
                } else {
                    setApprovals(prevApprovals => [...prevApprovals, ...approvalData])
                }
            } else {
                if (page === 1) {
                    setApprovals([])
                }
                setError('获取审批数据失败')
            }
        } catch (error) {
            console.error('获取审批数据失败:', error)
            if (page === 1) {
                setApprovals([])
            }
            setError('网络错误，请稍后重试')
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // 初始化数据
    useEffect(() => {
        fetchApprovals(1)
    }, [filters])



    // 加载更多
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            const nextPage = currentPage + 1
            fetchApprovals(nextPage)
        }
    }, [loadingMore, hasMore, currentPage, filters])

    // 上划到底部加载更多
    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current
            if (!container || loadingMore || !hasMore) return

            const { scrollTop, scrollHeight, clientHeight } = container
            // 当滚动到底部附近时触发加载更多
            if (scrollHeight - scrollTop - clientHeight < 50) {
                handleLoadMore()
            }
        }

        const container = containerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [loadingMore, hasMore, handleLoadMore])

    // 处理筛选条件变化
    const handleFilterChange = (e) => {
        const { name, value } = e.target
        setFilters(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // 获取司机列表
    const fetchDrivers = async () => {
        try {
            setLoadingDrivers(true)
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/adDriver/getDriverInfoList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            if (response.data && response.data.code === 200 && response.data.data) {
                setDrivers(response.data.data)
            } else {
                setDrivers([])
            }
        } catch (error) {
            console.error('获取司机列表失败:', error)
            setDrivers([])
        } finally {
            setLoadingDrivers(false)
        }
    }

    // 进入审批详情（打开弹窗）
    const handleApprovalDetail = (approval) => {
        setSelectedApproval(approval)
        setApprovalOpinion('')
        setSelectedDriverId(null)
        setDriveType(approval.driveType || 1)
        setApprovalModalVisible(true)
        // 如果默认是司机代驾，获取司机列表
        if (approval.driveType === 2) {
            fetchDrivers()
        }
    }

    // 处理审批意见变化
    const handleApprovalOpinionChange = (e) => {
        setApprovalOpinion(e.target.value)
    }

    // 处理驾驶方式变化
    const handleDriveTypeChange = (e) => {
        const newDriveType = parseInt(e.target.value)
        setDriveType(newDriveType)
        setSelectedDriverId(null)
        // 如果切换为司机代驾，获取司机列表
        if (newDriveType === 2) {
            fetchDrivers()
        }
    }

    // 提交审批
    const submitApproval = async (approvalStatus, reassignData = null) => {
        if (!selectedApproval) return

        // 当驾驶方式为司机代驾时，司机必选
        if (driveType === 2 && !selectedDriverId) {
            showToast('请选择司机', 'warning');
            return
        }

        // 当驳回时，审批意见必填
        if (approvalStatus === '2' && !approvalOpinion.trim()) {
            showToast('驳回时审批意见必填', 'warning');
            return
        }

        try {
            setSubmitting(true)
            setError(null)

            // 构建请求参数
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicationId: selectedApproval.applicationId,
                approvalStatus: approvalStatus,
                approvalOpinion: approvalOpinion || (approvalStatus === '1' ? '同意' : '驳回'),
                driveType: driveType,
                approverId: userInfo.user_id || '',
                approverName: userInfo.name || ''
            }

            // 如果是司机代驾，添加司机ID
            if (driveType === 2) {
                params.driverId = selectedDriverId
            }

            // 如果是改派，添加改派相关参数
            if (reassignData) {
                params.plateNumber = reassignData.plateNumber
                params.expectedStartTime = reassignData.expectedStartTime
                params.expectedEndTime = reassignData.expectedEndTime
                params.vehicleId = reassignData.vehicleId
            }

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/approve`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                showToast('审批成功', 'success');
                setApprovalModalVisible(false)
                setReassignModalVisible(false)
                // 刷新审批列表
                fetchApprovals(1)
            } else {
                showToast('审批失败:' + response.data.message, 'error');
            }
        } catch (error) {
            console.error('审批失败:', error)
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            setSubmitting(false)
        }
    }

    // 打开改派弹窗
    const handleReassign = () => {
        if (!selectedApproval) return
        
        // 格式化原申请单的时间为datetime-local格式
        const formatTimeForInput = (timeStr) => {
            // 假设时间格式为 'YYYY-MM-DD HH:mm:ss'
            if (!timeStr) return ''
            const [date, time] = timeStr.split(' ')
            return `${date}T${time.slice(0, 5)}`
        }
        
        // 初始化改派表单数据
        setReassignForm({
            expectedStartTime: formatTimeForInput(selectedApproval.expectedStartTime),
            expectedEndTime: formatTimeForInput(selectedApproval.expectedEndTime),
            plateNumber: '',
            vehicleId: ''
        })
        
        setAvailableVehicles([])
        setReassignModalVisible(true)
        // 初始化改派表单数据
        const formattedStartTime = formatTimeForInput(selectedApproval.expectedStartTime)
        const formattedEndTime = formatTimeForInput(selectedApproval.expectedEndTime)
        // 直接使用格式化后的时间调用接口获取可预约车辆
        if (formattedStartTime && formattedEndTime) {
            // 转换时间格式为 yyyy-MM-dd HH:mm:ss
            const formatTime = (timeStr) => {
                if (!timeStr) return ''
                return timeStr.replace('T', ' ') + ':00'
            }

            setLoadingVehicles(true)
            axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                startTime: formatTime(formattedStartTime),
                endTime: formatTime(formattedEndTime)
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    if (response.data && response.data.code === 200) {
                        setAvailableVehicles(response.data.data || [])
                    } else {
                        setAvailableVehicles([])
                    }
                })
                .catch(error => {
                    console.error('获取可预约车辆失败:', error)
                    setAvailableVehicles([])
                })
                .finally(() => {
                    setLoadingVehicles(false)
                })
        }
    }

    // 关闭改派弹窗
    const handleCloseReassignModal = () => {
        setReassignModalVisible(false)
        setReassignForm({
            expectedStartTime: '',
            expectedEndTime: '',
            plateNumber: '',
            vehicleId: ''
        })
        setAvailableVehicles([])
    }

    // 处理改派表单变化
    const handleReassignFormChange = (e) => {
        const { name, value } = e.target
        setReassignForm(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // 获取可预约的车辆
    const fetchAvailableVehicles = async () => {
        const { expectedStartTime, expectedEndTime } = reassignForm
        
        if (!expectedStartTime || !expectedEndTime) {
            setAvailableVehicles([])
            return
        }
        
        try {
            setLoadingVehicles(true)
            // 转换时间格式为 yyyy-MM-dd HH:mm:ss
            const formatTime = (timeStr) => {
                if (!timeStr) return ''
                return timeStr.replace('T', ' ') + ':00'
            }
            
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                startTime: formatTime(expectedStartTime),
                endTime: formatTime(expectedEndTime)
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            
            if (response.data && response.data.code === 200) {
                console.error('获取可预约车辆:', response.data.data)
                setAvailableVehicles(response.data.data || [])
            } else {
                setAvailableVehicles([])
            }
        } catch (error) {
            console.error('获取可预约车辆失败:', error)
            setAvailableVehicles([])
        } finally {
            setLoadingVehicles(false)
        }
    }

    // 处理车牌号选择变化
    const handlePlateNumberChange = (e) => {
        const selectedPlateNumber = e.target.value
        const selectedVehicle = availableVehicles.find(v => v.plateNumber === selectedPlateNumber)
        
        setReassignForm(prev => ({
            ...prev,
            plateNumber: selectedPlateNumber,
            vehicleId: selectedVehicle ? selectedVehicle.vehicleId : ''
        }))
    }

    // 提交改派审批
    const submitReassign = () => {
        const { expectedStartTime, expectedEndTime, plateNumber } = reassignForm
        
        // 校验开始时间必填
        if (!expectedStartTime) {
            showToast('请选择开始时间', 'warning');
            return
        }
        
        // 校验结束时间必填
        if (!expectedEndTime) {
            showToast('请选择结束时间', 'warning');
            return
        }
        
        // 校验车牌号必填
        if (!plateNumber) {
            showToast('请选择车牌号', 'warning');
            return
        }
        
        // 校验结束时间不能早于开始时间
        if (new Date(expectedEndTime) <= new Date(expectedStartTime)) {
            showToast('结束时间不能早于开始时间', 'warning');
            return
        }
        
        // 转换时间格式为 yyyy-MM-dd HH:mm:ss
        const formatTime = (timeStr) => {
            if (!timeStr) return ''
            return timeStr.replace('T', ' ') + ':00'
        }
        
        const formattedStartTime = formatTime(expectedStartTime)
        const formattedEndTime = formatTime(expectedEndTime)
        
        submitApproval('1', {
            plateNumber: plateNumber,
            expectedStartTime: formattedStartTime,
            expectedEndTime: formattedEndTime,
            vehicleId: reassignForm.vehicleId
        })
    }

    // 关闭弹窗
    const handleCloseModal = () => {
        setApprovalModalVisible(false)
        setSelectedApproval(null)
        setApprovalOpinion('')
        setSelectedDriverId(null)
        setDrivers([])
    }

    // 返回首页
    const handleBack = () => {
        navigate('/')
    }

    return (
        <div
            className="car-approval-page"
            ref={containerRef}
        >
            <h2>用车审批</h2>

            {/* 筛选条件 */}
            <div className="filter-container">
                    <div className="filter-item search-input">
                        <input
                            type="text"
                            name="plateNumber"
                            value={filters.plateNumber}
                            onChange={handleFilterChange}
                            placeholder="请输入车牌号码"
                        />
                </div>
            </div>

            {loading ? (
                <div className="loading-indicator">
                    <p>正在加载审批数据...</p>
                </div>
            ) : error ? (
                <div className="error-message">
                    <p>{error}</p>
                    <button className="retry-btn" onClick={() => fetchApprovals(1)}>重试</button>
                </div>
            ) : (
                <div className="approvals-list">
                    {approvals.length === 0 ? (
                        <div className="no-approvals">
                            <p>暂无审批数据</p>
                        </div>
                    ) : (
                        <>
                            <div className="approval-cards">
                                {approvals.map(approval => (
                                    <div key={approval.applicationId} className="approval-card">
                                        <div className="card-header">
                                            <h3>{approval.plateNumber}</h3>
                                            <span className="application-code">{approval.applicationNo}</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="card-info">
                                                <div className="info-item">
                                                    <span className="label">申请人:</span>
                                                    <span className="value">{approval.applicantName}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">申请人电话:</span>
                                                    <span className="value">{approval.applicantPhone}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">所属部门:</span>
                                                    <span className="value">{approval.applicantDept}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">驾驶方式:</span>
                                                    <span className="value">{approval.driveType === 1 ? '自驾' : '司机代驾'}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">申请类型:</span>
                                                    <span className="value">
                                                        {approval.isPersonal === 0 ? '本人申请' : '代人申请'}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">用车人:</span>
                                                    <span className="value">{approval.vehicleUserName}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">使用场景:</span>
                                                    <span className="value">
                                                        {approval.usageScenarios === 1 ? '员工外出' :
                                                            approval.usageScenarios === 2 ? '一般客户接待' :
                                                                approval.usageScenarios === 3 ? '重要客户接待' :
                                                                    approval.usageScenarios}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">申请时间:</span>
                                                    <span className="value">{approval.applyTime}</span>
                                                </div>
                                                <div className="info-item full-width">
                                                    <span className="label">用车时间:</span>
                                                    <span className="value">{approval.expectedStartTime} 至 {approval.expectedEndTime}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">出发地:</span>
                                                    <span className="value">{approval.expectedLocation}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">目的地:</span>
                                                    <span className="value">{approval.destination}</span>
                                                </div>
                                                {approval.passengers && approval.passengers.trim() !== '' && (
                                                    <div className="info-item">
                                                        <span className="label">同行人:</span>
                                                        <span className="value">{approval.passengers}</span>
                                                    </div>
                                                )}
                                                <div className="info-item full-width">
                                                    <span className="label">用车原因:</span>
                                                    <span className="value">{approval.reason}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            <button
                                                className="approve-btn"
                                                onClick={() => handleApprovalDetail(approval)}
                                                style={{
                                                    padding: '12px 20px',
                                                    backgroundColor: '#1890ff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    height: '40px'
                                                }}
                                            >
                                                用车审批
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {loadingMore ? (
                                <div className="loading-more">
                                    <p>加载中...</p>
                                </div>
                            ) : hasMore ? (
                                <div className="load-more-container">
                                    <button className="load-more-btn" onClick={handleLoadMore}>
                                        加载更多
                                    </button>
                                </div>
                            ) : (
                                <div className="no-more-data">
                                    <p>没有更多数据了</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            <div className="action-buttons">
                <button className="back-btn" onClick={handleBack}>返回首页</button>
            </div>

            {/* 审批详情弹窗 */}
            {approvalModalVisible && selectedApproval && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            用车审批_{selectedApproval.plateNumber}
                        </h3>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请单号:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.applicationNo}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>申请人姓名:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.applicantName}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>用车原因:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.reason}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    minWidth: '100px'
                                }}>驾驶方式:</span>
                                <div style={{
                                    display: 'flex',
                                    gap: '20px'
                                }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        cursor: 'pointer'
                                    }}>
                                        <input
                                            type="radio"
                                            name="driveType"
                                            value="1"
                                            checked={driveType === 1}
                                            onChange={handleDriveTypeChange}
                                        />
                                        自驾
                                    </label>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        cursor: 'pointer'
                                    }}>
                                        <input
                                            type="radio"
                                            name="driveType"
                                            value="2"
                                            checked={driveType === 2}
                                            onChange={handleDriveTypeChange}
                                        />
                                        司机代驾
                                    </label>
                                </div>
                            </div>
                            {/* 司机选择下拉框 */}
                            {driveType === 2 && (
                                <div style={{
                                    backgroundColor: '#f9f9f9',
                                    padding: '15px',
                                    borderRadius: '8px'
                                }}>
                                    <span style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: 'bold',
                                        color: 'red'
                                    }}>选择司机:</span>
                                    <select
                                        value={selectedDriverId || ''}
                                        onChange={(e) => setSelectedDriverId(e.target.value)}
                                        disabled={loadingDrivers}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <option value="">请选择司机</option>
                                        {loadingDrivers ? (
                                            <option value="">加载中...</option>
                                        ) : (
                                            drivers.map(driver => (
                                                <option key={driver.value} value={driver.value}>
                                                    {driver.label} ({driver.phone})
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            )}

                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px'
                            }}>
                                <span style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#666'
                                }}>用车时间:</span>
                                <span style={{
                                    display: 'block',
                                    color: '#333'
                                }}>{selectedApproval.expectedStartTime} 至 {selectedApproval.expectedEndTime}</span>
                            </div>


                        </div>

                        <div style={{
                            marginBottom: '30px'
                        }}>
                            <span style={{
                                display: 'block',
                                marginBottom: '10px',
                                fontWeight: 'bold',
                                color: '#666'
                            }}>审批意见:</span>
                            <textarea
                                value={approvalOpinion}
                                onChange={handleApprovalOpinionChange}
                                placeholder="请输入审批意见（选填）"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    minHeight: '100px'
                                }}
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px',
                            marginBottom: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={handleCloseModal}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '70px'
                                }}
                            >
                                取消
                            </button>
                            <button
                                onClick={() => submitApproval('2')}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '70px'
                                }}
                            >
                                驳回
                            </button>
                            <button
                                onClick={handleReassign}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#ffc107',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '70px'
                                }}
                            >
                                改派
                            </button>
                            <button
                                onClick={() => submitApproval('1')}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1,
                                    flex: 1,
                                    minWidth: '70px'
                                }}
                            >
                                通过
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 改派弹窗 */}
            {reassignModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1100
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        width: '90%',
                        maxWidth: '500px'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            改派车辆
                        </h3>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    开始时间: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="expectedStartTime"
                                    value={reassignForm.expectedStartTime}
                                    onChange={handleReassignFormChange}
                                    onBlur={fetchAvailableVehicles}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    结束时间: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="expectedEndTime"
                                    value={reassignForm.expectedEndTime}
                                    onChange={handleReassignFormChange}
                                    onBlur={fetchAvailableVehicles}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    车牌号: <span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    name="plateNumber"
                                    value={reassignForm.plateNumber}
                                    onChange={handlePlateNumberChange}
                                    required
                                    disabled={loadingVehicles || availableVehicles.length === 0}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">选择时间后显示</option>
                                    {loadingVehicles ? (
                                        <option value="">加载中...</option>
                                    ) : (
                                        availableVehicles.map((vehicle, index) => (
                                            <option key={index} value={vehicle.plateNumber}>
                                                {vehicle.plateNumber} {vehicle.modelName}
                                            </option>
                                        ))
                                    )}
                                </select>
                                {loadingVehicles && <div style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>正在加载车辆...</div>}
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '20px',
                            marginBottom: '20px'
                        }}>
                            <button
                                onClick={handleCloseReassignModal}
                                disabled={submitting}
                                style={{
                                    padding: '10px 30px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1
                                }}
                            >
                                取消
                            </button>
                            <button
                                onClick={submitReassign}
                                disabled={submitting}
                                style={{
                                    padding: '10px 30px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: submitting ? 0.6 : 1
                                }}
                            >
                                确定
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}